import { useEffect, useState } from 'react';
import { ShareNetwork, DownloadSimple, Copy, XLogo } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { drawShareCard, type ShareCardData } from '@/lib/shareCard';
import { fmtPct, withSign } from '@/lib/format';

/*
 * Share a closed trade or a session as an image (lib/shareCard.ts).
 * Guillaume, 2026-09-22. Preview first, then: download, copy the image, the
 * phone's own share sheet where the browser has one, or a post on X.
 */
export function ShareButton({ data, label, fileName }: {
  data: ShareCardData;
  label?: string;
  fileName: string;
}) {
  const { t } = useTranslation('traders');
  const [open, setOpen] = useState(false);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    let made: string | null = null;
    drawShareCard(data).then((b) => {
      if (!alive || !b) return;
      made = URL.createObjectURL(b);
      setBlob(b);
      setUrl(made);
    });
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
    // The card is drawn from what is on screen when it is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const file = blob ? new File([blob], `${fileName}.png`, { type: 'image/png' }) : null;
  const canNativeShare = !!file && typeof navigator !== 'undefined' &&
    typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] });

  const download = () => {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}.png`;
    a.click();
  };
  const copy = async () => {
    if (!blob) return;
    try {
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      toast.success(t('share.copied'));
    } catch {
      toast.error(t('share.copyFailed'));
    }
  };
  const nativeShare = async () => {
    if (!file) return;
    try {
      await navigator.share({ files: [file], text: t('share.nativeText') });
    } catch { /* the user closed the sheet */ }
  };
  const postOnX = () => {
    const pct = `${withSign(data.headline >= 0, fmtPct(data.headline, 2))}`;
    const text = t('share.xText', { title: data.title, pct });
    window.open(`https://x.com/intent/post?text=${encodeURIComponent(text)}&url=${encodeURIComponent('https://app.copyfi.ai')}`,
      '_blank', 'noopener');
  };

  return (
    <>
      <Button variant="outline" size="sm" className="gap-2" onClick={(e) => { e.stopPropagation(); setOpen(true); }}>
        <ShareNetwork size={16} />
        {label ?? t('share.button')}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('share.title')}</DialogTitle>
          </DialogHeader>
          {url ? (
            <img src={url} alt={t('share.cardAlt')} className="w-full rounded-lg border border-border/40" />
          ) : (
            <div className="w-full aspect-[1200/675] rounded-lg bg-muted/30 animate-pulse" />
          )}
          <div className="flex flex-wrap gap-2 justify-end">
            <Button variant="outline" size="sm" className="gap-2" onClick={download} disabled={!url}>
              <DownloadSimple size={16} /> {t('share.download')}
            </Button>
            <Button variant="outline" size="sm" className="gap-2" onClick={copy} disabled={!blob}>
              <Copy size={16} /> {t('share.copyImage')}
            </Button>
            {canNativeShare && (
              <Button variant="outline" size="sm" className="gap-2" onClick={nativeShare}>
                <ShareNetwork size={16} /> {t('share.shareSheet')}
              </Button>
            )}
            <Button size="sm" className="gap-2" onClick={postOnX}>
              <XLogo size={16} /> {t('share.postOnX')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
